import {
  configureStore,
  createAction,
  EnhancedStore,
  StoreEnhancer,
  ThunkDispatch,
  Tuple,
  UnknownAction,
} from '@reduxjs/toolkit';
import dateFilterReducer from '../slices/dateFilterSlice';
import genericFilterReducer from '../slices/genericFilterSlice';
import guestFilterReducer from '../slices/guestFilterSlice';
import portsGalleryReducer, { PortsGalleryState } from '../slices/portsGallerySlice';
import portsGalleryTabsReducer, { PortsGalleryTabsState } from '../slices/portsGalleryTabsSlice';
import myAreaCommonConfigSliceReducer from '../slices/myAreaCommonConfigSlice';
import myAreaFilterReducer from '../slices/myAreaFilterSlice';
import myAreaNotificationsReducer from '../slices/myAreaNotificationSlice';
import myAreaDrawerReducer from '../slices/myAreaDrawersSlice';
import myAreaCartReducer from '../slices/myAreaCartSlice';
import errorManagerReducer from '../slices/errorManagerSlice';
import {
  ErrorManagerState,
  GenericConfigType,
  IActiveFilterState,
  ICabinPreferenceResponseCruisesR,
  ICabinTypesRedux,
  ICabinTypesResponseCruises,
  ICategoriestRedux,
  ICruiseData,
  IGuestFilterState,
  IPriceListResponseCruises,
  MyAreaCartState,
  MyAreaCommonConfigState,
  MyAreaDrawersState,
  MyAreaFilterState,
  MyAreaNotificationsState,
} from '../../../../../interfaces/src/lib/interfaces/index';
import {
  ChangeDatesModalState,
  DateFilterState,
  DealsConfigType,
  FilterOptionSliceType,
  GenericFilterState,
  LastFilterUpdatedState,
  OccupancyState,
  PaginationState,
  PaymentState,
  PromotionsState,
  SearchBarState,
  SearchRequestParams,
  SortingState,
} from '../../../../../interfaces/src/lib/types/index';
import facetFilterReducer, { FacetFilterState } from '../slices/facetFilterSlice';
import sortingViewPriceReducer, { SortingViewPriceState } from '../slices/sortingViewPriceSlice';
import sortingFilterByReducer, { SortingFilterByState } from '../slices/sortingFilterBySlice';
import searchButtonHandlingReducer, {
  SearchButtonHandlingState,
} from '../slices/searchButtonHandlingSlice';
import searchIndexTypeSliceReducer, { SearchIndexTypeState } from '../slices/searchIndexTypeSlice';
import searchBarStateSliceReducer from '../slices/searchBarSlice';
import searchReducer, { SearchState } from '../slices/searchSlice';
import { createEpicMiddleware } from 'redux-observable';
import { epics } from '../epics/epics';
import cruiseCardReducer, { CruiseCardState } from '../slices/cruiseCardSlice';
import { activeFilterReducer } from '../slices/activeFilterSlice';
import bookingParamsReducer, { IBookingParams } from '../slices/bookingParamsSlice';
import itinerarySliceReducer, { ItineraryDetailsState } from '../slices/itinerarySlice';
import occupancyReducer from '../slices/occupancySlice';
import priceTypesReducer from '../slices/priceTypeSlice';
import categoriesReducer from '../slices/categoriesSlice';
import cabinTypesReducer from '../slices/cabinTypesSlice';
import cabinTypeSorterReducer from '../slices/cabinTypeSorterSlice';
import dealsConfigReducer from '../slices/dealsConfigSlice';
import cruiseDataReducer from '../slices/cruiseDataSlice';
import cabinPreferenceReducer from '../slices/cabinPreferencesSlice';
import bookingStepsReducer, { BookingStepsType } from '../slices/bookingStepsSlice';
import paginationReducer from '../slices/paginationSlice';
import deckViewReducer, { DeckViewType } from '../slices/deckViewSlice';
import analyticsReducer from '../slices/analyticsSlice';
import { lastFilterUpdatedReducer } from '../slices/lastFilterUpdate';
import { genericConfigReducer } from '../slices/genericConfigSlice';
import { dateslayerReducer, DateslayerState } from '../slices/dateslayerSlice';
import { multiFilterReducer, MultiFilterState } from '../slices/multiFilterSlice';
import changeDatesModalReducer from '../slices/changeDatesModalSlice';
import flightDataReducer, { IFlightData } from '../slices/departureflights';
import transferReducer, { ITransfer } from '../slices/transferSlice';
import bookingContentFragmentReducer, {
  BookingContentFragmentSliceState,
} from '../slices/bookingContentFragmentSlice';
import promotionsReducer from '../slices/promotionsSlice';
import myCruiseDataReducer, { MyCruiseState } from '../slices/myCruiseDataSlice';
import excursionsCardReducer, { ExcursionsCardState } from '../slices/excursionsCardSlice';
import {
  algoliaContextComponentReducer,
  IAlgoliaContextComponentState,
} from '../slices/algoliaContextComponentSlice';

import { bookingApi } from '../services/bookingAPI';
import navigationMenuReducer, { NavigationMenuState } from '../slices/navigationMenuSlice';
import addressManagementReducer, { IAddressManagement } from '../slices/addressManagement';
import paymentReducer from '../slices/paymentSlice';
import { checkinApi } from '../services/checkinsAPI';
import { cartListener } from '../middleware/cartMiddleware';

declare global {
  interface Window {
    searchbarLoaded: boolean;
    store: EnhancedStore<
      {
        transferData: ITransfer;
        lastFilterUpdated: LastFilterUpdatedState;
        genericConfigs: GenericConfigType;
        dateFilter: DateFilterState;
        facetFilter: FacetFilterState;
        genericFilter: {
          numberOfNights: FilterOptionSliceType[];
          promotion: FilterOptionSliceType[];
          tag: FilterOptionSliceType[];
          flyAndCruise: FilterOptionSliceType[];
        };
        guestFilter: IGuestFilterState;
        sortingViewPrice: SortingViewPriceState;
        sortingFilterBy: SortingFilterByState;
        search: SearchRequestParams;
        searchIndexTypeState: SearchIndexTypeState;
        searchIndex: FacetFilterState;
        multiFilter: MultiFilterState;
        cruiseCard: CruiseCardState;
        excursionsCard: ExcursionsCardState;
        activeFilter: IActiveFilterState;
        promotions: PromotionsState;
        bookingParams: IBookingParams;
        priceTypes: IPriceListResponseCruises;
        categories: ICategoriestRedux;
        cabinTypes: ICabinTypesResponseCruises;
        occupancy: OccupancyState;
        searchButtonHandling: SearchButtonHandlingState;
        searchBarState: SearchBarState;
        cabinPreferencesStore: ICabinPreferenceResponseCruisesR;
        itineraryDetails: ItineraryDetailsState;
        bookingStepsStore: BookingStepsType;
        pagination: PaginationState;
        deckView: DeckViewType;
        analytics: { [key: string]: any };
        cabinTypeSorter: SortingState;
        dateslayer: DateslayerState;
        dealsConfig: DealsConfigType;
        flightData: IFlightData;
        cruiseData: ICruiseData;
        algoliaContextComponent: IAlgoliaContextComponentState;
        bookingContentFragment: BookingContentFragmentSliceState;
        addressManagement: IAddressManagement;
        myCruise: MyCruiseState;
        myAreaCart: MyAreaCartState;
        navigationMenuData: NavigationMenuState;
        errorManager: ErrorManagerState;
      },
      UnknownAction,
      Tuple<
        [
          StoreEnhancer<{
            dispatch: ThunkDispatch<
              {
                lastFilterUpdated: LastFilterUpdatedState;
                genericConfigs: GenericConfigType;
                dateFilter: DateFilterState;
                genericFilter: {
                  numberOfNights: FilterOptionSliceType[];
                  promotion: FilterOptionSliceType[];
                  tag: FilterOptionSliceType[];
                  flyAndCruise: FilterOptionSliceType[];
                };
                guestFilter: IGuestFilterState;
                sortingViewPrice: SortingViewPriceState;
                sortingFilterBy: SortingFilterByState;
                search: SearchRequestParams;
                facetFilter: FacetFilterState;
                searchIndexTypeState: SearchIndexTypeState;
                multiFilter: MultiFilterState;
                cruiseCard: CruiseCardState;
                excursionsCard: ExcursionsCardState;
                activeFilter: IActiveFilterState;
                promotions: PromotionsState;
                bookingParams: IBookingParams;
                priceTypes: IPriceListResponseCruises;
                categories: ICategoriestRedux;
                cabinTypes: ICabinTypesResponseCruises;
                occupancy: OccupancyState;
                searchButtonHandling: SearchButtonHandlingState;
                cabinPreferencesStore: ICabinPreferenceResponseCruisesR;
                itineraryDetails: ItineraryDetailsState;
                bookingStepsStore: BookingStepsType;
                pagination: PaginationState;
                deckView: DeckViewType;
                analytics: { [key: string]: any };
                cabinTypeSorter: SortingState;
                dateslayer: DateslayerState;
                dealsConfig: DealsConfigType;
                transferData: ITransfer;
                flightData: IFlightData;
                cruiseData: ICruiseData;
                bookingContentFragment: BookingContentFragmentSliceState;
                algoliaContextComponent: IAlgoliaContextComponentState;
                portsGallery: PortsGalleryState;
                myCruise: MyCruiseState;
                myAreaCommonConfig: MyAreaCommonConfigState;
                myAreaNotifications: MyAreaNotificationsState;
                myAreaFilter: MyAreaFilterState;
                myAreaDrawers: MyAreaDrawersState;
                myAreaCart: MyAreaCartState;
                navigationMenuData: NavigationMenuState;
                addressManagement: IAddressManagement;
                errorManager: ErrorManagerState;
              },
              undefined,
              UnknownAction
            >;
          }>,
          StoreEnhancer
        ]
      >
    >;
  }
}

// Create the epic middleware and add it to the store
const epicMiddleware = createEpicMiddleware();

export interface RootState {
  genericConfigs: GenericConfigType;
  lastFilterUpdated: LastFilterUpdatedState;
  dateFilter: DateFilterState;
  sortingViewPrice: SortingViewPriceState;
  sortingFilterBy: SortingFilterByState;
  search: SearchState;
  genericFilter: GenericFilterState;
  guestFilter: IGuestFilterState;
  facetFilter: FacetFilterState;
  multiFilter: MultiFilterState;
  cruiseCard: CruiseCardState;
  excursionsCard: ExcursionsCardState;
  activeFilter: IActiveFilterState;
  promotions: PromotionsState;
  bookingParams: IBookingParams;
  priceTypes: IPriceListResponseCruises;
  categories: ICategoriestRedux;
  cabinTypes: ICabinTypesRedux;
  occupancy: OccupancyState;
  searchButtonHandling: SearchButtonHandlingState;
  searchIndexTypeState: SearchIndexTypeState;
  searchBarState: SearchBarState;
  cabinPreferencesStore: ICabinPreferenceResponseCruisesR;
  bookingStepsStore: BookingStepsType;
  deckView: DeckViewType;
  itineraryDetails: ItineraryDetailsState;
  pagination: PaginationState;
  cabinTypeSorter: SortingState;
  dateslayer: DateslayerState;
  changeDatesModal: ChangeDatesModalState;
  dealsConfig: DealsConfigType;
  flightData: IFlightData;
  transferData: ITransfer;
  cruiseData: ICruiseData;
  bookingContentFragment: BookingContentFragmentSliceState;
  algoliaContextComponent: IAlgoliaContextComponentState;
  portsGallery: PortsGalleryState;
  portsGalleryTabs: PortsGalleryTabsState;
  myCruise: MyCruiseState;
  myAreaCommonConfig: MyAreaCommonConfigState;
  myAreaNotifications: MyAreaNotificationsState;
  myAreaFilter: MyAreaFilterState;
  myAreaDrawers: MyAreaDrawersState;
  myAreaCart: MyAreaCartState;
  navigationMenuData: NavigationMenuState;
  addressManagement: IAddressManagement;
  payment: PaymentState;
  errorManager: ErrorManagerState;
}

export const store =
  window.store ||
  configureStore({
    reducer: {
      changeDatesModal: changeDatesModalReducer,
      lastFilterUpdated: lastFilterUpdatedReducer,
      genericConfigs: genericConfigReducer,
      dateFilter: dateFilterReducer,
      genericFilter: genericFilterReducer,
      guestFilter: guestFilterReducer,
      sortingViewPrice: sortingViewPriceReducer,
      sortingFilterBy: sortingFilterByReducer,
      search: searchReducer,
      facetFilter: facetFilterReducer,
      multiFilter: multiFilterReducer,
      cruiseCard: cruiseCardReducer,
      excursionsCard: excursionsCardReducer,
      activeFilter: activeFilterReducer,
      promotions: promotionsReducer,
      bookingParams: bookingParamsReducer,
      priceTypes: priceTypesReducer,
      categories: categoriesReducer,
      cabinTypes: cabinTypesReducer,
      occupancy: occupancyReducer,
      searchButtonHandling: searchButtonHandlingReducer,
      searchBarState: searchBarStateSliceReducer,
      searchIndexTypeState: searchIndexTypeSliceReducer,
      cabinPreferencesStore: cabinPreferenceReducer,
      itineraryDetails: itinerarySliceReducer,
      bookingStepsStore: bookingStepsReducer,
      pagination: paginationReducer,
      deckView: deckViewReducer,
      analytics: analyticsReducer,
      cabinTypeSorter: cabinTypeSorterReducer,
      dateslayer: dateslayerReducer,
      dealsConfig: dealsConfigReducer,
      flightData: flightDataReducer,
      transferData: transferReducer,
      cruiseData: cruiseDataReducer,
      bookingContentFragment: bookingContentFragmentReducer,
      algoliaContextComponent: algoliaContextComponentReducer,
      portsGallery: portsGalleryReducer,
      portsGalleryTabs: portsGalleryTabsReducer,
      myCruise: myCruiseDataReducer,
      myAreaCommonConfig: myAreaCommonConfigSliceReducer,
      myAreaNotifications: myAreaNotificationsReducer,
      myAreaFilter: myAreaFilterReducer,
      myAreaDrawers: myAreaDrawerReducer,
      myAreaCart: myAreaCartReducer,
      navigationMenuData: navigationMenuReducer,
      addressManagement: addressManagementReducer,
      [bookingApi.reducerPath]: bookingApi.reducer,
      [checkinApi.reducerPath]: checkinApi.reducer,
      payment: paymentReducer,
      errorManager: errorManagerReducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(epicMiddleware)
        .concat(bookingApi.middleware)
        .concat(checkinApi.middleware)
        .concat(cartListener.middleware),
  });

window.store = store;

// Run the epics
epicMiddleware.run(epics as any);

export type AppDispatch = typeof store.dispatch;
export const resetFromPriceType = createAction('resetFromPriceType');
export const resetFromOccupancy = createAction('resetFromOccupancy');
export const resetFromCabinType = createAction<{ cabinNumber: number }>('resetFromCabinType');
export const resetFromDeck = createAction<{ cabinNumber: number }>('resetFromDeck');
export const resetFromSummary = createAction('resetFromSummary');
export const resetFromPassengerDetails = createAction('resetFromPassengerDetails');
export const resetFromCabinCategories = createAction<{ cabinNumber: number }>(
  'resetFromCabinCategories'
);
export const resetMyCruiseData = createAction('resetMyCruiseData');

// Export reducers for external use
export { portsGalleryTabsReducer };
